import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, pu, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, Save, Coffee, Copy } from "lucide-react";

const VUOTO = { off: false, from: "", to: "" };

/** Calendario turni: persone per giorni, si compila una volta e si ripete ogni settimana. */
export default function Turni() {
  const { user, settings } = useApp();
  const [d, setD] = useState(null);
  const [bozza, setBozza] = useState({});
  const comanda = user?.role === "titolare" || user?.role === "direttore";

  const load = useCallback(() => {
    g("/shifts").then((r) => {
      setD(r);
      setBozza(Object.fromEntries(r.rows.map((x) => [x.staff_id, x.week])));
    }).catch(() => setD(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!d) return <p className="text-sm text-slate-500">Caricamento…</p>;

  if (!d.enabled) {
    return (
      <div className="fade-up space-y-4" data-testid="turni-spento">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Calendario turni</h1>
        <Card className="p-6">
          <p className="text-sm text-slate-600">
            Il calendario turni è spento. Se lo accendi, per ogni persona scrivi una volta la settimana
            (per esempio «riposo» il lunedì, «18:30–24:00» il sabato) e resta così ogni settimana, con la
            possibilità di correggere un singolo giorno quando serve.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Lo accendi da <b>Dipendenti e credenziali</b>, nel riquadro «Come entra il personale».
          </p>
        </Card>
      </div>
    );
  }

  const cambia = (sid, i, campo, valore) => {
    setBozza((b) => {
      const week = [...(b[sid] || Array(7).fill(VUOTO))];
      week[i] = { ...week[i], [campo]: valore };
      if (campo === "off" && valore) week[i] = { off: true, from: "", to: "" };
      return { ...b, [sid]: week };
    });
  };

  const salva = async (r) => {
    try {
      await pu(`/shifts/${r.staff_id}`, { week: bozza[r.staff_id], by: user?.name || "Titolare" });
      toast.success(`Turni di ${r.name} salvati`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const copiaGiorno = (sid, i) => {
    const g0 = (bozza[sid] || [])[i] || VUOTO;
    setBozza((b) => ({ ...b, [sid]: Array(7).fill({ ...g0 }) }));
    toast.info("Giorno copiato su tutta la settimana: ricordati di salvare");
  };

  return (
    <div className="fade-up space-y-6" data-testid="turni-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Personale</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Calendario turni</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Una settimana fissa per ogni persona: si compila una volta e si ripete. Chi è di riposo lo vede
          subito, e se hai acceso il blocco d'accesso {settings?.shift_lock ? "(è acceso)" : "(ora è spento)"} chi
          non è in turno non entra nel gestionale.
        </p>
      </div>

      <div className="space-y-4">
        {d.rows.map((r) => (
          <Card key={r.staff_id} className="p-5" data-testid={`turni-persona-${r.staff_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-semibold">{r.name}</p>
                <p className="text-xs text-slate-500">
                  {r.role}
                  {r.state !== "attivo" && <span className="ml-1 font-semibold text-amber-700">· {r.state}</span>}
                  {r.today && !r.today.off && r.today.from && (
                    <span className="ml-1 font-semibold text-emerald-700">· oggi {r.today.from}–{r.today.to}</span>
                  )}
                  {r.today?.off && <span className="ml-1 font-semibold text-slate-600">· oggi riposo</span>}
                </p>
              </div>
              {comanda && (
                <Button size="sm" onClick={() => salva(r)} data-testid={`turni-save-${r.staff_id}`}>
                  <Save className="mr-1 h-3.5 w-3.5" /> Salva
                </Button>
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {(bozza[r.staff_id] || Array(7).fill(VUOTO)).map((gg, i) => (
                <div
                  key={`${r.staff_id}-${i}`}
                  className={`rounded-lg border p-3 ${i === d.today_index ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
                  data-testid={`turni-giorno-${r.staff_id}-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-600">{d.giorni[i]}</p>
                    {comanda && (
                      <button className="text-slate-400 hover:text-slate-700" title="Copia su tutta la settimana"
                              onClick={() => copiaGiorno(r.staff_id, i)} data-testid={`turni-copia-${r.staff_id}-${i}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {gg.off ? (
                    <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-slate-500">
                      <Coffee className="h-4 w-4" /> Riposo
                    </p>
                  ) : (
                    <div className="mt-2 flex items-center gap-1.5">
                      <Input className="h-9 px-2 text-sm" type="time" value={gg.from || ""} disabled={!comanda}
                             onChange={(e) => cambia(r.staff_id, i, "from", e.target.value)}
                             data-testid={`turni-from-${r.staff_id}-${i}`} />
                      <span className="text-xs text-slate-400">→</span>
                      <Input className="h-9 px-2 text-sm" type="time" value={gg.to || ""} disabled={!comanda}
                             onChange={(e) => cambia(r.staff_id, i, "to", e.target.value)}
                             data-testid={`turni-to-${r.staff_id}-${i}`} />
                    </div>
                  )}
                  {comanda && (
                    <button
                      className="mt-2 text-[11px] font-semibold text-slate-500 underline"
                      onClick={() => cambia(r.staff_id, i, "off", !gg.off)}
                      data-testid={`turni-riposo-${r.staff_id}-${i}`}
                    >
                      {gg.off ? "Metti un orario" : "Segna riposo"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {!comanda && (
        <p className="text-xs text-slate-500" data-testid="turni-sola-lettura">
          Vedi i turni di tutti, ma li cambia solo la direzione.
        </p>
      )}
    </div>
  );
}
